import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import MenuBar from "../components/MenuBar";
import { canAccessSalesDataImport } from "../features/salesDataImport/access";
import SalesImportNav from "../features/salesDataImport/components/SalesImportNav";
import {
  SALES_IMPORT_BRANCH_OPTIONS,
  salesImportBranchDisplayName,
} from "../features/salesDataImport/config/salesImportBranchIds";
import {
  fetchBrandByBranchReport,
  flattenBrandByBranchForExport,
  pickAmount,
} from "../features/salesDataImport/services/brandByBranchReportService";
import { formatFirebaseError } from "../features/salesDataImport/utils/firebaseError";
import "./SalesDataImportCenter.css";

const BRANCH_STORAGE_KEY = "sdic-brand-by-branch-branches";
const VOUCHER_STORAGE_KEY = "sdic-brand-by-branch-voucher";

function readStoredVoucherType() {
  try {
    return localStorage.getItem(VOUCHER_STORAGE_KEY) || "all";
  } catch {
    return "all";
  }
}

function readSelectedBranches() {
  try {
    const raw = localStorage.getItem(BRANCH_STORAGE_KEY);
    if (!raw) return [...SALES_IMPORT_BRANCH_OPTIONS];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return [...SALES_IMPORT_BRANCH_OPTIONS];
    return parsed.filter((id) => SALES_IMPORT_BRANCH_OPTIONS.includes(id));
  } catch {
    return [...SALES_IMPORT_BRANCH_OPTIONS];
  }
}

function writeSelectedBranches(ids) {
  try {
    localStorage.setItem(BRANCH_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

function fm(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export default function BrandByBranchReportPage() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const nowYear = new Date().getFullYear();
  const [dark, setDark] = useState(() => localStorage.getItem("sdic-dark") === "1");
  const [year, setYear] = useState(nowYear);
  const [amountType, setAmountType] = useState("sales");
  const [voucherType, setVoucherType] = useState(readStoredVoucherType);
  const [voucherOptions, setVoucherOptions] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState(readSelectedBranches);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [report, setReport] = useState(null);
  const [search, setSearch] = useState("");

  const yearOptions = useMemo(() => [nowYear - 1, nowYear, nowYear + 1], [nowYear]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return report?.rows || [];
    return (report?.rows || []).filter((r) => String(r.label || "").toLowerCase().includes(term));
  }, [report, search]);

  const amountHeading =
    amountType === "purchase" ? "Purchase" : amountType === "net" ? "Net" : "Sales";

  const toggleBranch = (branchId) => {
    setSelectedBranches((prev) => {
      const next = prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId];
      writeSelectedBranches(next.length ? next : [...SALES_IMPORT_BRANCH_OPTIONS]);
      return next.length ? next : [...SALES_IMPORT_BRANCH_OPTIONS];
    });
  };

  const loadReport = useCallback(async () => {
    if (!selectedBranches.length) {
      toast.error("Select at least one branch.");
      return;
    }
    setLoading(true);
    setProgress(0);
    try {
      const out = await fetchBrandByBranchReport(
        { year, branchIds: selectedBranches, amountType, voucherType },
        (n) => setProgress(n)
      );
      setReport(out);
      setVoucherOptions(out.voucherTypeOptions || []);
      if (!out.rows.length) {
        toast.error("No import lines found for this year and branch selection.");
      } else {
        toast.success(
          `${out.brandCount} brand(s) · ${out.lineCount.toLocaleString()} lines from BrandName column`
        );
      }
    } catch (e) {
      toast.error(formatFirebaseError(e));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [year, selectedBranches, amountType, voucherType]);

  useEffect(() => {
    if (!canAccessSalesDataImport(user)) return;
    loadReport();
  }, [user, loadReport]);

  useEffect(() => {
    try {
      localStorage.setItem(VOUCHER_STORAGE_KEY, voucherType || "all");
    } catch {
      /* ignore */
    }
  }, [voucherType]);

  useEffect(() => {
    localStorage.setItem("sdic-dark", dark ? "1" : "0");
  }, [dark]);

  useEffect(() => {
    writeSelectedBranches(selectedBranches);
  }, [selectedBranches]);

  useEffect(() => {
    if (
      voucherType !== "all" &&
      voucherOptions.length > 0 &&
      !voucherOptions.includes(voucherType)
    ) {
      setVoucherType("all");
    }
  }, [voucherOptions, voucherType]);

  const exportExcel = () => {
    if (!report?.rows?.length) {
      toast.error("Nothing to export.");
      return;
    }
    const rows = flattenBrandByBranchForExport(report);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BrandByBranch");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `brand-by-branch-${year}${voucherType !== "all" ? `-${voucherType.replace(/\s+/g, "-")}` : ""}.xlsx`
    );
  };

  if (!canAccessSalesDataImport(user)) {
    return <div className="p-6 text-center">Access denied — Admin or Storekeeper only.</div>;
  }

  return (
    <div className={`sdic-root${dark ? " sdic-dark" : ""}`}>
      <MenuBar />
      <header className="sdic-header">
        <div>
          <h1>Brand by Branch</h1>
          <p>
            {amountHeading} amounts grouped by the <strong>BrandName</strong> column from sales import
            lines
            {voucherType !== "all" ? (
              <>
                {" "}
                · voucher: <strong>{voucherType}</strong>
              </>
            ) : null}
          </p>
        </div>
        <button type="button" className="sdic-btn sdic-btn-secondary" onClick={() => setDark((d) => !d)}>
          {dark ? "Light" : "Dark"}
        </button>
      </header>

      <div className="sdic-body">
        <SalesImportNav active="brand-by-branch" showBranchFilter={false} />

        <section className="sdic-panel">
          <div className="sdic-week-banner" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
            <label>
              Year
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                style={{ padding: "0.4rem", minWidth: "100px" }}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Voucher type
              <select
                value={voucherType}
                onChange={(e) => setVoucherType(e.target.value)}
                style={{ padding: "0.4rem", minWidth: "180px" }}
              >
                <option value="all">All voucher types</option>
                {voucherOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Amount
              <select
                value={amountType}
                onChange={(e) => setAmountType(e.target.value)}
                style={{ padding: "0.4rem", minWidth: "120px" }}
              >
                <option value="sales">Sales</option>
                <option value="purchase">Purchase</option>
                <option value="net">Net (Sales − Purchase)</option>
              </select>
            </label>
            <button
              type="button"
              className="sdic-btn sdic-btn-primary"
              disabled={loading}
              onClick={loadReport}
            >
              {loading ? `Loading… ${progress.toLocaleString()} lines` : "Refresh"}
            </button>
            <button
              type="button"
              className="sdic-btn sdic-btn-secondary"
              disabled={!report?.rows?.length}
              onClick={exportExcel}
            >
              Export Excel
            </button>
            <input
              className="sdic-search"
              style={{ margin: 0, maxWidth: 220 }}
              placeholder="Search brand…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={{ marginTop: "0.75rem" }}>
            <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.35rem" }}>
              Branches
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem 1.25rem" }}>
              {SALES_IMPORT_BRANCH_OPTIONS.map((id) => (
                <label key={id} style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.85rem" }}>
                  <input
                    type="checkbox"
                    checked={selectedBranches.includes(id)}
                    onChange={() => toggleBranch(id)}
                  />
                  {salesImportBranchDisplayName(id)}
                </label>
              ))}
              <button
                type="button"
                className="sdic-btn sdic-btn-secondary"
                style={{ padding: "0.2rem 0.6rem", fontSize: "0.78rem" }}
                onClick={() => setSelectedBranches([...SALES_IMPORT_BRANCH_OPTIONS])}
              >
                All
              </button>
            </div>
          </div>

          <p style={{ fontSize: "0.83rem", color: "var(--sdic-muted)", margin: "0.75rem 0 0" }}>
            Reads <strong>BrandName</strong> exactly as stored in import line chunks — no Brand Master
            or Products lookup. <strong>Sales</strong> uses the same per-line amount as Sales Advanced
            Reports (<strong>Amount</strong>, then <strong>BillAmount</strong> if Amount is missing) for
            every matched line. <strong>Purchase</strong> = <strong>Inward Supply</strong> subset only.{" "}
            <strong>Net</strong> = Sales − Purchase. Uses <strong>Trans Date</strong> for the year filter.
          </p>
        </section>

        {report && (
          <>
            <section className="sdic-panel">
              <h2 className="sdic-panel-title">
                Year {year} · BrandName · {amountHeading}
                {voucherType !== "all" ? ` · ${voucherType}` : ""}
              </h2>
              <div className="sdic-validation-grid">
                <div className="sdic-val-box sdic-val-valid">
                  Total sales
                  <br />₹ {fm(report.grandSales)}
                </div>
                <div className="sdic-val-box" style={{ borderColor: "var(--sdic-accent)" }}>
                  Total purchase
                  <br />₹ {fm(report.grandPurchase)}
                </div>
                <div className="sdic-val-box sdic-val-dup">
                  Net
                  <br />₹ {fm(report.grandNet)}
                </div>
                <div className="sdic-val-box">
                  Brands
                  <br />
                  {(report.brandCount || 0).toLocaleString()}
                </div>
                <div className="sdic-val-box">
                  Lines
                  <br />
                  {(report.lineCount || 0).toLocaleString()}
                </div>
                {(report.rowsWithoutBrandName || 0) > 0 && (
                  <div className="sdic-val-box" style={{ borderColor: "var(--sdic-accent)" }}>
                    Blank BrandName
                    <br />
                    {(report.rowsWithoutBrandName || 0).toLocaleString()}
                  </div>
                )}
              </div>
            </section>

            <section className="sdic-panel">
              <h2 className="sdic-panel-title">BrandName × branch amounts</h2>
              <div className="sdic-table-wrap" style={{ maxHeight: "70vh" }}>
                <table className="sdic-table">
                  <thead>
                    <tr>
                      <th style={{ position: "sticky", left: 0, zIndex: 2, background: "inherit" }}>
                        BrandName
                      </th>
                      {(report.branches || []).map((br) => (
                        <th key={br.id} style={{ textAlign: "right" }}>
                          {br.label}
                        </th>
                      ))}
                      <th style={{ textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr
                        key={row.key}
                        style={row.isBlank ? { fontStyle: "italic", opacity: 0.85 } : undefined}
                      >
                        <td style={{ position: "sticky", left: 0, fontWeight: 600, background: "inherit" }}>
                          {row.label}
                        </td>
                        {(report.branches || []).map((br) => {
                          const cell = row.byBranch[br.id] || { salesAmount: 0, purchaseAmount: 0 };
                          return (
                            <td key={`${row.key}-${br.id}`} style={{ textAlign: "right" }}>
                              ₹ {fm(pickAmount(cell, amountType))}
                            </td>
                          );
                        })}
                        <td style={{ textAlign: "right" }}>
                          <strong>
                            ₹{" "}
                            {fm(
                              pickAmount(
                                {
                                  salesAmount: row.totalSales,
                                  purchaseAmount: row.totalPurchase,
                                },
                                amountType
                              )
                            )}
                          </strong>
                        </td>
                      </tr>
                    ))}
                    {!filteredRows.length && (
                      <tr>
                        <td colSpan={(report.branches?.length || 0) + 2} style={{ color: "var(--sdic-muted)" }}>
                          No rows match your search.
                        </td>
                      </tr>
                    )}
                    {!!filteredRows.length && (
                      <tr style={{ fontWeight: 700, background: "var(--sdic-bg-soft, rgba(0,0,0,0.04))" }}>
                        <td style={{ position: "sticky", left: 0, background: "inherit" }}>Total</td>
                        {(report.branches || []).map((br) => {
                          const t = report.branchTotals?.[br.id] || { salesAmount: 0, purchaseAmount: 0 };
                          return (
                            <td key={`tot-${br.id}`} style={{ textAlign: "right" }}>
                              ₹ {fm(pickAmount(t, amountType))}
                            </td>
                          );
                        })}
                        <td style={{ textAlign: "right" }}>
                          ₹{" "}
                          {fm(
                            pickAmount(
                              { salesAmount: report.grandSales, purchaseAmount: report.grandPurchase },
                              amountType
                            )
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
